/* eslint-disable no-nested-ternary */
import React, {useCallback, useState, useEffect} from 'react'
import styled from 'styled-components'
import {ifProp, palette} from 'styled-tools'
import {media} from 'styled-bootstrap-grid'
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon'
import ArrowGoBackLineIcon from 'remixicon-react/ArrowGoBackLineIcon'
import {useCheckout} from 'src/context/CheckoutContext'
import {Link, Image} from 'components/common'
import {H5, PLarge, PSmall} from 'components/typography'
import {CornerButton, UnderlineButton} from 'components/buttons'

import {isTouchDevice} from 'src/utils/isTouch'
import {formatNumber} from 'src/utils/numbers'
import {theme} from 'src/themes'
import {useRecoilState} from 'recoil'
import {CheckoutLineVariants} from './CheckoutLineVariants'
import {generatePath, paths} from '../../../utils/paths'
import type {CheckoutLineFragment} from '../../../api/saleor/generated'
import {CurrentQuantityItem} from './store'

const LineCard = styled.div`
  position: relative;
  margin-bottom: 24px;
  background-color: ${palette('white')};
  border: 1px solid ${palette('black')};
  padding: 20px;
`

const CardWrapper = styled.div``

const ProductInfo = styled(Link)`
  display: flex;
  flex-direction: row;
  gap: 20px;
  ${media.sm`
    margin-top: 15px;
  `}
`

const ProductInfoText = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const ProductVariant = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 111px;
  @media (max-width: ${theme.screenSize.sizeMD}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    margin-left: 0;
    margin-top: 20px;
  }
`

const QtyPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${palette('black90')};
`
const PriceWrapper = styled.div`
  width: fit-content;
  margin-right: 10px;
`
interface BackgroundProps {
  show: boolean
}

const DeleteBackground = styled.div<BackgroundProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: ${ifProp({show: true}, 'flex', 'none')};
  align-items: center;
  justify-content: center;
  z-index: 99;
  opacity: 0.9;
  background-color: ${palette('background')};
`

const DeleteButton = styled(CornerButton)<{hover: boolean}>`
  top: 1px;
  right: 1px;
  opacity: ${({hover}) => (isTouchDevice ? 1 : hover ? 1 : 0)};
  transition: opacity 0.15s;
`

interface Props {
  line: CheckoutLineFragment
}

export const CheckoutLineCard = ({line}: Props): JSX.Element => {
  const [remove, setRemove] = useState(false)
  const [hover, setHover] = useState(false)
  const handleMouseOver = useCallback(() => setHover(true), [])
  const handleMouseOut = useCallback(() => setHover(false), [])
  const [timerRemove, setTimerRemove] = useState<number | null>(null)
  const {cartUpdate} = useCheckout()
  const variantId = line.variant.id
  const [inputQuantity, setInputQuantity] = useRecoilState(
    CurrentQuantityItem(variantId),
  )
  useEffect(() => {
    setInputQuantity(line.quantity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.quantity])
  useEffect(() => {
    const fn = (): void => {
      setInputQuantity(line.quantity)
    }
    document.body.addEventListener('CheckoutError', fn)

    return () => {
      document.body.removeEventListener('CheckoutError', fn)
    }
  }, [line.quantity, setInputQuantity])
  const currentQty = inputQuantity
  const image =
    line?.variant?.media?.[0]?.url || line?.variant?.product?.media?.[0]?.url

  const handleDeleteLine = useCallback(() => {
    if (!remove) {
      setRemove(true)
      setTimerRemove(
        window.setTimeout(() => cartUpdate({[variantId]: 0}), 2000),
      )
    } else {
      setRemove(false)
      if (timerRemove) {
        window.clearTimeout(timerRemove)
      }
    }
  }, [cartUpdate, remove, timerRemove, variantId])
  const price = line?.totalPrice?.gross?.amount ?? null
  const perUnitPrice = price && currentQty ? price / currentQty : null

  return (
    <LineCard onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
      <CardWrapper>
        <ProductInfo
          href={generatePath(paths.product, line?.variant?.product?.slug)}
        >
          {image ? (
            <Image
              src={image}
              alt={line?.variant?.product?.name}
              width={100}
              height={80}
              layout="responsive"
              objectFit="contain"
            />
          ) : null}
          <ProductInfoText>
            <PSmall color="black60">{line?.variant?.sku}</PSmall>
            <PLarge bold>{line?.variant?.product?.name}</PLarge>
          </ProductInfoText>
        </ProductInfo>

        <ProductVariant>
          <CheckoutLineVariants
            line={line}
            qty={currentQty}
            variantId={variantId}
          />

          {price && perUnitPrice ? (
            <PriceWrapper>
              <PSmall color="black60">Samtals</PSmall>
              <H5>{formatNumber(perUnitPrice * inputQuantity)} KR.</H5>
              <QtyPriceWrapper>
                <PSmall>{formatNumber(inputQuantity)} stykki</PSmall> /
                <PSmall>{formatNumber(perUnitPrice)} kr. /stk.</PSmall>
              </QtyPriceWrapper>
            </PriceWrapper>
          ) : null}
        </ProductVariant>
      </CardWrapper>

      <DeleteBackground show={remove}>
        <UnderlineButton
          text="Draga aðgerð til baka"
          icon={<ArrowGoBackLineIcon size={14} />}
          onClick={handleDeleteLine}
        />
      </DeleteBackground>

      <DeleteButton
        hover={hover}
        icon={<DeleteBinLineIcon size={16} />}
        onClick={handleDeleteLine}
      />
    </LineCard>
  )
}
